import { useMemo, useState, useCallback } from 'react';
import { Box } from '@mui/material';

import { snackbar, Button, SearchForm, TableContainer, TableHeader } from '@occc-app/design-system';
import { Tab, Tabs } from '@occc/ui-design-system';
import {
  useAssociateLocationsWithRetailerNetworkUsersMutation,
  useAssociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation,
  RetailerNetworkLocationFilter,
  RetailerNetworkLocationGroupFilter,
  useDisassociateLocationsWithRetailerNetworkUsersMutation,
  useDisassociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation,
  Retailer_Network_User_Role,
} from '@occc/graphql';
import { useTabQueryParam } from '@occc/utils-query-params';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { UpdateRetailerNetworkUser } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import RetailerNetworkUserDetailsLocationGroupsTable from './retailer-network-user-details-location-groups/retailer-network-user-details-location-groups-table.container';
import RetailerNetworkUserDetailsLocationsTable from './retailer-network-user-details-locations/retailer-network-user-details-locations-table.container';
import { RetailerNetworkAddUserToLocationsDialog } from './retailer-network-user-details-locations/retailer-network-add-user-to-locations-dialog.component';
import { RetailerNetworkAddUserToLocationGroupsDialog } from './retailer-network-user-details-location-groups/retailer-network-add-user-to-location-groups-dialog.component';
import {
  LOCATIONS_PARAM_NAMES,
  LOCATION_GROUPS_PARAM_NAMES,
  RETAILER_NETWORK_USER_LOCATIONS_AND_GROUPS_TAB_PARAM,
} from './retailer-network-user-details-locations-and-groups.constants';

import { RemoveUserFromLocationDialog } from '../../components/remove-user-from-location-dialog.component';
import { RemoveUserFromLocationGroupDialog } from '../../components/remove-user-from-location-group-dialog.component';
import { RetailerNetworkUserLocationsAndLocationGroupsSectionTab } from '../retailer-network-user-details-page.types';
import {
  DEFAULT_RETAILER_NETWORK_USER_LOCATIONS_AND_GROUPS_TAB,
  RETAILER_NETWORK_USER_LOCATIONS_AND_GROUPS_TABS,
  tabLabelsMap,
} from '../retailer-network-user-details-page.constants';
import { useGetIsUserPermittedToAssociateAndDisassociate } from '../hooks/use-get-is-user-permitted-to-associate-and-disassociate.hook';

const transitionStyle = 'transform 500ms cubic-bezier(.75, 0, .25, 1)';

const targetingBoxStyles = {
  transition: transitionStyle,
  overflow: 'hidden',
  width: '100%',
};

const commonStyles = (isActive: boolean) => ({
  transition: transitionStyle,
  width: '100%',
  overflow: 'hidden',
  maxHeight: isActive ? '100%' : '0px',
});

const locationsTabStyles = (isActive: boolean) => ({
  ...commonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(-100%, 0)',
});

const locationGroupsTabStyles = (isActive: boolean) => ({
  ...commonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(100%, 0)',
});

type RetailerNetworkUserDetailsLocationAndGroupsProps = {
  retailerNetworkUuid: string;
  retailerNetworkUserUuid: string;
  retailerNetworkUserRole: Retailer_Network_User_Role;
  openCommerceUserUuid: string;
};

export const RetailerNetworkUserDetailsLocationsAndLocationGroups = ({
  retailerNetworkUuid,
  retailerNetworkUserUuid,
  retailerNetworkUserRole,
  openCommerceUserUuid,
}: RetailerNetworkUserDetailsLocationAndGroupsProps) => {
  const [activeTab, setActiveTab] =
    useTabQueryParam<RetailerNetworkUserLocationsAndLocationGroupsSectionTab>(
      RETAILER_NETWORK_USER_LOCATIONS_AND_GROUPS_TAB_PARAM,
      DEFAULT_RETAILER_NETWORK_USER_LOCATIONS_AND_GROUPS_TAB,
      RETAILER_NETWORK_USER_LOCATIONS_AND_GROUPS_TABS,
    );
  const handleTabChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: RetailerNetworkUserLocationsAndLocationGroupsSectionTab,
  ) => {
    setActiveTab(value);
  };

  const [locationsSearch, setLocationsSearch] = useSearchParams(LOCATIONS_PARAM_NAMES);
  const [locationGroupsSearch, setLocationGroupsSearch] = useSearchParams(
    LOCATION_GROUPS_PARAM_NAMES,
  );

  const [addUserToLocationsDialogOpen, setAddUserToLocationsDialogOpen] = useState<boolean>(false);
  const [addUserToLocationGroupsDialogOpen, setAddUserToLocationGroupsDialogOpen] =
    useState<boolean>(false);
  const [removeUserFromLocationDialogOpen, setRemoveUserFromLocationDialogOpen] =
    useState<boolean>(false);
  const [removeUserFromLocationGroupDialogOpen, setRemoveUserFromLocationGroupDialogOpen] =
    useState<boolean>(false);

  const [uuidToRemove, setUuidToRemove] = useState<string | null>(null);

  const isCurrentUserPermittedToAssociateAndDisassociate =
    useGetIsUserPermittedToAssociateAndDisassociate(retailerNetworkUserRole);

  const tableActions = useMemo(() => {
    if (activeTab === 'groups') {
      return (
        <Box display='flex'>
          <SearchForm
            onSubmit={({ search }) => setLocationGroupsSearch(search)}
            initialValues={{ search: '' }}
          />
          <Can I={UpdateRetailerNetworkUser({ uuid: retailerNetworkUuid })}>
            {isCurrentUserPermittedToAssociateAndDisassociate && (
              <Button
                variant='contained'
                sx={{ minWidth: 'fit-content', marginLeft: 2 }}
                onClick={() => setAddUserToLocationGroupsDialogOpen(true)}
                role='button'
              >
                Add to Location Group
              </Button>
            )}
          </Can>
        </Box>
      );
    }

    return (
      <Box display='flex'>
        <SearchForm
          onSubmit={({ search }) => setLocationsSearch(search)}
          initialValues={{ search: '' }}
        />
        <Can I={UpdateRetailerNetworkUser({ uuid: retailerNetworkUuid })}>
          {isCurrentUserPermittedToAssociateAndDisassociate && (
            <Button
              variant='contained'
              sx={{ minWidth: 'fit-content', marginLeft: 2 }}
              onClick={() => setAddUserToLocationsDialogOpen(true)}
              role='button'
            >
              Add to location
            </Button>
          )}
        </Can>
      </Box>
    );
  }, [
    activeTab,
    setLocationGroupsSearch,
    setLocationsSearch,
    retailerNetworkUuid,
    isCurrentUserPermittedToAssociateAndDisassociate,
  ]);

  const [associateLocationsWithRetailerNetwork] =
    useAssociateLocationsWithRetailerNetworkUsersMutation({
      onCompleted: () => {
        snackbar.success('User has been successfully added');
      },
      onError: (error) => {
        snackbar.error(error.message);
      },
    });

  const [associateLocationGroupsWithRetailerNetwork] =
    useAssociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation({
      onCompleted: () => {
        snackbar.success('User has been successfully added');
      },
      onError: (error) => {
        snackbar.error(error.message);
      },
    });

  const [
    disassociateLocationsWithRetailerNetworkUsers,
    { loading: isRemoveUserFromLocationInProgress },
  ] = useDisassociateLocationsWithRetailerNetworkUsersMutation({
    onCompleted: () => {
      snackbar.success('User has been successfully removed from location');
    },
  });

  const [
    disassociateLocationGroupsWithRetailerNetworkUsers,
    { loading: isRemoveUserFromLocationGroupInProgress },
  ] = useDisassociateRetailerNetworkLocationGroupsWithRetailerNetworkUsersMutation({
    onCompleted: () => {
      snackbar.success('User has been successfully removed from location group');
    },
  });

  const associateLocationsWithUser = async (locationUuids: string[]) => {
    associateLocationsWithRetailerNetwork({
      variables: {
        locationUuids: locationUuids,
        retailerNetworkUserUuids: [retailerNetworkUserUuid],
        retailerNetworkUuid: retailerNetworkUuid,
      },
      refetchQueries: ['retailerNetworkLocations'],
    });
  };

  const associateLocationGroupsWithUser = async (locationGroupsUuids: string[]) => {
    associateLocationGroupsWithRetailerNetwork({
      variables: {
        locationGroupUuids: locationGroupsUuids,
        retailerNetworkUserUuids: [retailerNetworkUserUuid],
        retailerNetworkUuid: retailerNetworkUuid,
      },
      refetchQueries: ['retailerNetworkLocationGroups'],
    });
  };

  const onRemoveUserFromLocationDialogConfirm = useCallback(async () => {
    if (uuidToRemove) {
      await disassociateLocationsWithRetailerNetworkUsers({
        variables: {
          retailerNetworkUuid,
          locationUuids: [uuidToRemove],
          retailerNetworkUserUuids: [retailerNetworkUserUuid],
        },
        refetchQueries: ['retailerNetworkLocations'],
      });

      setUuidToRemove(null);
      setRemoveUserFromLocationDialogOpen(false);
    }
  }, [
    uuidToRemove,
    disassociateLocationsWithRetailerNetworkUsers,
    retailerNetworkUuid,
    retailerNetworkUserUuid,
  ]);

  const onRemoveUserFromLocationGroupDialogConfirm = useCallback(async () => {
    if (uuidToRemove) {
      await disassociateLocationGroupsWithRetailerNetworkUsers({
        variables: {
          retailerNetworkUuid,
          locationGroupUuids: [uuidToRemove],
          retailerNetworkUserUuids: [retailerNetworkUserUuid],
        },
        refetchQueries: ['retailerNetworkLocationGroups'],
      });

      setUuidToRemove(null);
      setRemoveUserFromLocationGroupDialogOpen(false);
    }
  }, [
    uuidToRemove,
    disassociateLocationGroupsWithRetailerNetworkUsers,
    retailerNetworkUuid,
    retailerNetworkUserUuid,
  ]);

  const handleRemoveUserFromLocation = (userUuid: string) => {
    setRemoveUserFromLocationDialogOpen(true);
    setUuidToRemove(userUuid);
  };

  const handleRemoveUserFromLocationGroup = (userUuid: string) => {
    setRemoveUserFromLocationGroupDialogOpen(true);
    setUuidToRemove(userUuid);
  };

  const locationsTabFilter: RetailerNetworkLocationFilter = {
    retailerNetworkUuidEquals: retailerNetworkUuid,
    retailerNetworkUserUuidEquals: retailerNetworkUserUuid,
    openCommerceUserUuidEquals: openCommerceUserUuid,
    search: locationsSearch,
  };

  const locationGroupsTabFilter: RetailerNetworkLocationGroupFilter = {
    retailerNetworkUuidEquals: retailerNetworkUuid,
    retailerNetworkUserUuidEquals: retailerNetworkUserUuid,
    openCommerceUserUuidEquals: openCommerceUserUuid,
    search: locationGroupsSearch,
  };

  return (
    <>
      <TableContainer>
        <TableHeader
          disableTitleTypography
          tabs={
            <Tabs
              variant='scrollable'
              scrollButtons='auto'
              value={activeTab}
              onChange={handleTabChange}
            >
              <Tab
                value={
                  'locations' satisfies RetailerNetworkUserLocationsAndLocationGroupsSectionTab
                }
                label={tabLabelsMap['locations']}
              />
              <Tab
                value={'groups' satisfies RetailerNetworkUserLocationsAndLocationGroupsSectionTab}
                label={tabLabelsMap['groups']}
              />
            </Tabs>
          }
          actions={tableActions}
        />
        <Box style={targetingBoxStyles}>
          <Box
            id={'locations' satisfies RetailerNetworkUserLocationsAndLocationGroupsSectionTab}
            style={locationsTabStyles(activeTab === 'locations')}
          >
            <RetailerNetworkUserDetailsLocationsTable
              filter={locationsTabFilter}
              retailerNetworkUuid={retailerNetworkUuid}
              handleRemoveUserFromLocation={handleRemoveUserFromLocation}
              isCurrentUserPermittedToDisassociate={
                isCurrentUserPermittedToAssociateAndDisassociate
              }
            />
          </Box>
          <Box
            id={'groups' satisfies RetailerNetworkUserLocationsAndLocationGroupsSectionTab}
            style={locationGroupsTabStyles(activeTab === 'groups')}
          >
            <RetailerNetworkUserDetailsLocationGroupsTable
              filter={locationGroupsTabFilter}
              retailerNetworkUuid={retailerNetworkUuid}
              handleRemoveUserFromLocationGroup={handleRemoveUserFromLocationGroup}
              isCurrentUserPermittedToDisassociate={
                isCurrentUserPermittedToAssociateAndDisassociate
              }
            />
          </Box>
        </Box>
      </TableContainer>

      {addUserToLocationsDialogOpen && (
        <RetailerNetworkAddUserToLocationsDialog
          retailerNetworkUuid={retailerNetworkUuid}
          open={addUserToLocationsDialogOpen}
          setDialogOpen={setAddUserToLocationsDialogOpen}
          selectLocationUuids={associateLocationsWithUser}
        />
      )}

      {addUserToLocationGroupsDialogOpen && (
        <RetailerNetworkAddUserToLocationGroupsDialog
          retailerNetworkUuid={retailerNetworkUuid}
          open={addUserToLocationGroupsDialogOpen}
          setDialogOpen={setAddUserToLocationGroupsDialogOpen}
          selectLocationGroupUuids={associateLocationGroupsWithUser}
        />
      )}

      {removeUserFromLocationDialogOpen && (
        <RemoveUserFromLocationDialog
          open={removeUserFromLocationDialogOpen}
          setOpen={setRemoveUserFromLocationDialogOpen}
          onConfirm={onRemoveUserFromLocationDialogConfirm}
          isRemoveUserFromLocationInProgress={isRemoveUserFromLocationInProgress}
        />
      )}

      {removeUserFromLocationGroupDialogOpen && (
        <RemoveUserFromLocationGroupDialog
          open={removeUserFromLocationGroupDialogOpen}
          setOpen={setRemoveUserFromLocationGroupDialogOpen}
          onConfirm={onRemoveUserFromLocationGroupDialogConfirm}
          isRemoveUserFromLocationGroupInProgress={isRemoveUserFromLocationGroupInProgress}
        />
      )}
    </>
  );
};
